// Design shell switcher — V3 (desktop bento) and V4 (mobile bottom-tab).
// Writes <html data-version="v3|v4"> so CSS can layer per-shell rules.
// V1 and V2 were removed after the second design round; anything still
// tagged v1/v2 in localStorage is silently upgraded to v3.

import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "gabruz-version";
const VersionCtx  = createContext(null);
const VALID       = ["v3", "v4"];

function applyVersion(v) {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.version = v;
  }
}

// Mobile devices (narrow viewport OR mobile UA) → V4 bottom-tab shell.
// Everything else → V3 bento desktop.
function detectDefaultVersion() {
  if (typeof window === "undefined") return "v3";
  const narrow = window.matchMedia &&
    window.matchMedia("(max-width: 900px)").matches;
  const ua = (navigator.userAgent || "").toLowerCase();
  const mobileUA = /mobi|android|iphone|ipod|ipad/.test(ua);
  return (narrow || mobileUA) ? "v4" : "v3";
}

function readInitial() {
  if (typeof localStorage === "undefined") return detectDefaultVersion();
  const saved = localStorage.getItem(STORAGE_KEY);
  if (VALID.includes(saved)) return saved;
  return detectDefaultVersion();
}

export function VersionProvider({ children }) {
  const [version, setVersionState] = useState(readInitial);

  useEffect(() => { applyVersion(version); }, [version]);

  function setVersion(next) {
    if (!VALID.includes(next)) return;
    setVersionState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }
  function toggleVersion() {
    setVersion(version === "v3" ? "v4" : "v3");
  }

  return (
    <VersionCtx.Provider value={{ version, setVersion, toggleVersion }}>
      {children}
    </VersionCtx.Provider>
  );
}

export function useVersion() {
  const ctx = useContext(VersionCtx);
  if (!ctx) throw new Error("useVersion must be used inside <VersionProvider>");
  return ctx;
}
